import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Download, Mail, Ban, Wallet, ExternalLink, Upload, Paperclip, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { care, fail, rm, can, openFile } from './careApi';
import { Badge, Btn, Input, Select, Table, Loading } from './careUi';
import { ask } from './AskDialog';

const METHODS = ['Cash', 'Bank Transfer', 'Cheque', 'Other'];
const PROOF_METHODS = ['Bank Transfer', 'Cheque', 'Other'];

export const ReceiptActions = ({ rc, onChanged }) => {
  const email = async () => {
    const to = await ask({ title: `Email receipt ${rc.receipt_no}`, label: 'Email address', type: 'email', defaultValue: rc.payer_email || '', confirm: 'Send' });
    if (to === null) return;
    try { const { data } = await care.post(`/receipts/${rc.id}/email`, { email: to }); toast.success(`Receipt emailed to ${data.to}`); } catch (e) { fail(e); }
  };
  const voidIt = async () => {
    const reason = await ask({ title: `Void receipt ${rc.receipt_no}`, message: 'The payment will be reversed and the invoice balance restored. The receipt is kept and marked VOIDED.', label: 'Reason', required: true, minLength: 3, danger: true, confirm: 'Void receipt' });
    if (!reason) return;
    try { await care.post(`/receipts/${rc.id}/void`, { reason }); toast.success('Receipt voided'); onChanged && onChanged(); } catch (e) { fail(e); }
  };
  return (
    <div className="flex justify-end gap-1">
      <Btn variant="ghost" className="!px-2 !py-1" onClick={() => openFile(`/receipts/${rc.id}/pdf`)} data-testid={`rc-pdf-${rc.receipt_no}`}><Download className="w-4 h-4" /></Btn>
      <Btn variant="ghost" className="!px-2 !py-1" onClick={email} data-testid={`rc-email-${rc.receipt_no}`}><Mail className="w-4 h-4" /></Btn>
      {can('admin') && rc.status !== 'voided' && <Btn variant="ghost" className="!px-2 !py-1" onClick={voidIt} data-testid={`rc-void-${rc.receipt_no}`}><Ban className="w-4 h-4 text-rose-600" /></Btn>}
    </div>
  );
};

export default function InvoiceDialog({ id, onClose, onChanged }) {
  const [inv, setInv] = useState(null);
  const [pay, setPay] = useState({ amount: '', method: 'Cash', reference: '', paid_at: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState('');
  const [proof, setProof] = useState(null);
  const needsProof = PROOF_METHODS.includes(pay.method);
  const load = () => care.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(fail);
  useEffect(() => { if (id) { setInv(null); load(); } }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { load(); onChanged && onChanged(); };
  const payUrl = inv && `${window.location.origin}/care/pay/${inv.pay_token}`;
  const open = inv && inv.status !== 'Cancelled' && inv.balance > 0;

  const record = async () => {
    setBusy('pay');
    try {
      const { data } = await care.post(`/invoices/${id}/payments`, { ...pay, amount: Number(pay.amount), proof_id: needsProof ? proof?.proof_id : '' });
      toast.success(`Payment recorded. Receipt ${data.receipt_no} issued.`);
      setPay({ ...pay, amount: '', reference: '' }); setProof(null); refresh();
    } catch (e) { fail(e); } finally { setBusy(''); }
  };
  const uploadProof = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('File is larger than 10 MB');
    setBusy('proof');
    const fd = new FormData();
    fd.append('file', file);
    try { const { data } = await care.post(`/invoices/${id}/payment-proof`, fd); setProof(data); toast.success('Payment proof uploaded'); }
    catch (e) { fail(e); } finally { setBusy(''); }
  };
  const email = async () => {
    setBusy('email');
    try { const { data } = await care.post(`/invoices/${id}/email`, { origin_url: window.location.origin }); toast.success(`Invoice emailed to ${data.to}`); }
    catch (e) { fail(e); } finally { setBusy(''); }
  };
  const cancel = async () => {
    const reason = await ask({ title: 'Cancel invoice', label: 'Reason for cancelling', required: true, minLength: 3, danger: true, confirm: 'Cancel invoice' });
    if (!reason) return;
    try { await care.post(`/invoices/${id}/cancel`, { reason }); toast.success('Invoice cancelled'); refresh(); } catch (e) { fail(e); }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle>Invoice {inv?.invoice_no}</DialogTitle></DialogHeader>
        {!inv ? <Loading /> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge s={inv.status} testid="invoice-status" />
              <span className="text-slate-600">{inv.resident_name} · {inv.resident_code}</span>
              <span className="text-slate-400">Period {inv.period_label} · Issued {inv.invoice_date} · Due {inv.due_date}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {inv.items.map((i, k) => <tr key={k} className="border-b border-slate-100"><td className="py-2">{i.label}</td><td className="py-2 text-right tabular-nums">{rm(i.amount)}</td></tr>)}
                <tr><td className="pt-3 font-semibold">Total amount payable</td><td className="pt-3 text-right font-semibold tabular-nums" data-testid="invoice-total">{rm(inv.total)}</td></tr>
                <tr><td className="text-slate-500">Amount paid</td><td className="text-right tabular-nums" data-testid="invoice-paid">{rm(inv.amount_paid)}</td></tr>
                <tr><td className="font-bold">Outstanding balance</td><td className="text-right font-bold tabular-nums" data-testid="invoice-balance">{rm(inv.balance)}</td></tr>
              </tbody>
            </table>
            <div className="flex flex-wrap gap-2">
              <Btn variant="ghost" onClick={() => openFile(`/invoices/${id}/pdf?origin=${encodeURIComponent(window.location.origin)}`)} data-testid="invoice-pdf"><Download className="w-4 h-4" />PDF / Print</Btn>
              <Btn variant="ghost" loading={busy === 'email'} onClick={email} data-testid="invoice-email"><Mail className="w-4 h-4" />Email invoice</Btn>
              {open && <Btn variant="ghost" onClick={() => { navigator.clipboard.writeText(payUrl); toast.success('Pay Now link copied'); }} data-testid="invoice-copy-pay-link"><Copy className="w-4 h-4" />Copy Pay Now link</Btn>}
              {open && <a href={payUrl} target="_blank" rel="noreferrer" data-testid="invoice-pay-now"><Btn><ExternalLink className="w-4 h-4" />Pay Now</Btn></a>}
              {inv.status !== 'Cancelled' && inv.amount_paid === 0 && <Btn variant="ghost" onClick={cancel} data-testid="invoice-cancel"><Ban className="w-4 h-4 text-rose-600" />Cancel</Btn>}
            </div>
            {open && (
              <div className="rounded-xl bg-slate-50 p-4" data-testid="invoice-record-payment">
                <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Wallet className="w-4 h-4" />Record offline payment</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <Input label="Amount (RM)" type="number" min="0" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} testid="pay-amount" />
                  <Select label="Method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} options={METHODS} testid="pay-method" />
                  <Input label="Reference" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} testid="pay-reference" />
                  <Input label="Date" type="date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} testid="pay-date" />
                </div>
                {needsProof && (
                  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="pay-proof-section">
                    <label className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${proof ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-dashed border-teal-400 bg-white text-teal-800 hover:bg-teal-50'}`}>
                      <Upload className="w-4 h-4" />{busy === 'proof' ? 'Uploading…' : proof ? 'Replace proof' : `Upload ${pay.method === 'Cheque' ? 'cheque' : 'payment'} proof *`}
                      <input type="file" className="hidden" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={busy === 'proof'}
                        onChange={(e) => { uploadProof(e.target.files[0]); e.target.value = ''; }} data-testid="pay-proof-input" />
                    </label>
                    {proof ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-testid="pay-proof-file"><Paperclip className="w-3.5 h-3.5" />{proof.filename}
                        <button type="button" onClick={() => setProof(null)} className="text-slate-400 hover:text-rose-600" data-testid="pay-proof-remove"><X className="w-3.5 h-3.5" /></button></span>
                    ) : <span className="text-xs text-slate-500">Required for {pay.method}. PDF, JPG, PNG or WEBP, max 10 MB.</span>}
                  </div>
                )}
                <Btn className="mt-3" loading={busy === 'pay'} disabled={!(Number(pay.amount) > 0) || (needsProof && !proof)} onClick={record} data-testid="pay-submit">Record payment & issue receipt</Btn>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Payments</p>
              <Table testid="invoice-payments" rows={inv.payments} cols={[
                { k: 'paid_at', h: 'Date', r: (p) => p.paid_at.slice(0, 10) }, { k: 'method', h: 'Method' }, { k: 'reference', h: 'Reference' },
                { k: 'receipt_no', h: 'Receipt' }, { k: 'status', h: 'Status', r: (p) => <Badge s={p.status} /> },
                { k: 'amount', h: 'Amount', right: true, r: (p) => rm(p.amount) },
                { k: 'proof', h: 'Proof', r: (p) => p.proof ? <button type="button" onClick={() => openFile(`/payments/${p.id}/proof`)} className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline" data-testid={`pay-proof-view-${p.receipt_no}`}><Paperclip className="w-3.5 h-3.5" />View</button> : <span className="text-slate-300">—</span> },
                { k: 'x', h: '', right: true, r: (p) => p.receipt_id && <ReceiptActions rc={{ id: p.receipt_id, receipt_no: p.receipt_no, payer_email: p.payer_email, status: p.status === 'voided' ? 'voided' : 'issued' }} onChanged={refresh} /> },
              ]} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
